/**
 * Error Service - TypeScript Migration
 * Handles error messages and displays them using the notification service
 */

import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { log, logAttributes } from './logging.service';
import { notificationService } from './notification.service';
import { errorDialogEventBus } from '../new-components/hf-error-dialog/ErrorDialogManager';
import { AxiosError } from 'axios';

// union type for all possible error types
type ErrorInput = Error | AxiosError | ErrorStatus | string | unknown;

interface ErrorStatus {
    status?: number;
    data?: {
        customMessage?: string;
        errorMessage?: string;
        errorCode?: string;
    };
    errorMessage?: string;
    response?: ErrorStatus;
}

interface ParsedError {
    message: string;
    status?: ErrorStatus;
    shouldCapture: boolean;
}

export class ErrorService {
    /**
     * Report an error to Sentry.
     *
     * HTTP failures are already reported — with endpoint-level grouping — by the axios
     * response interceptor in `axiosSetup.ts`. Capturing them again here filed every failed
     * request as two separate issues, so they are skipped.
     */
    reportError(error: any) {
        if (this.isAxiosError(error)) {
            return;
        }
        Sentry.captureException(error);
    }
    /**
     * Get translated error string from error code
     */
    getErrorStringFromCode(errorCode: string): string {
        if (errorCode === 'USER_DOSNT_EXISTS') {
            return i18next.t('CREDENTIALS_ERROR');
        }
        return i18next.t(errorCode);
    }

    /**
     * Get error code from status object
     */
    getErrorCodeFromStatus(status: ErrorStatus): string | undefined {
        return status.data?.errorCode;
    }

    /**
     * Check if error is NOT_ENOUGH_ACTIVE_EMPLOYEES
     */
    isNotEnoughActiveEmployees(error?: ErrorInput): boolean {
        if (!error) { return false; }

        // convert to ErrorStatus first
        let status: ErrorStatus | undefined;

        if (this.isAxiosError(error)) {
            status = this.axiosErrorToStatus(error as AxiosError);
        } else if (this.isErrorStatus(error)) {
            status = error as ErrorStatus;
        } else {
            return false;
        }

        const errorCode = status.data?.errorCode || status.response?.data?.errorCode;
        return status.status === 412 && errorCode === 'NOT_ENOUGH_ACTIVE_EMPLOYEES';
    }

    /**
     * Check if error is a 403 SURVEY_NOT_ENOUGH_PERMISSIONS (user not allowed to view
     * a survey's results). This is a server-side safety net, not a normal flow — the UI
     * should hide the results action via `canViewResults`. When it does surface, treat it
     * as "no access", not as a generic page error.
     */
    isNotEnoughSurveyPermissions(error?: ErrorInput): boolean {
        if (!error) { return false; }

        let status: ErrorStatus | undefined;

        if (this.isAxiosError(error)) {
            status = this.axiosErrorToStatus(error as AxiosError);
        } else if (this.isErrorStatus(error)) {
            status = error as ErrorStatus;
        } else {
            return false;
        }

        const errorCode = status.data?.errorCode || status.response?.data?.errorCode;
        return status.status === 403 && errorCode === 'SURVEY_NOT_ENOUGH_PERMISSIONS';
    }

    /**
     * Resolve a user-facing error message from any supported error input.
     */
    resolveErrorMessage(error: ErrorInput): string {
        return this.parseError(error).message;
    }

    /**
     * Show error modal as a dialog
     * handles Error, AxiosError, ErrorStatus, string, and unknown types
     */
    showErrorModal(error: ErrorInput): void {
        // tslint:disable-next-line:no-console
        console.trace('Error service showErrorModal call stack:');

        const parsed = this.parseError(error);

        // only capture error if needed
        if (parsed.shouldCapture) {
            Sentry.captureException(error);
        }

        this.logUserFacingError(error, parsed, 'dialog');

        errorDialogEventBus.show(parsed.message);
    }

    /**
     * Show error dialog with custom message
     */
    showErrorDialog(message: string): void {
        errorDialogEventBus.show(message);
    }

    /**
     * Show error as toast notification
     * handles Error, AxiosError, ErrorStatus, string, and unknown types
     */
    showErrorToast(error: ErrorInput): void {
        // tslint:disable-next-line:no-console
        console.trace('Error service showErrorToast call stack:');

        const parsed = this.parseError(error);

        // only capture error if needed
        if (parsed.shouldCapture) {
            Sentry.captureException(error);
        }

        this.logUserFacingError(error, parsed, 'toast');

        notificationService.error(parsed.message);
    }

    /**
     * Show success toast notification
     */
    showSuccessToast(message: string): void {
        notificationService.success(message);
    }

    /**
     * Show info toast notification
     */
    showInfoToast(message: string): void {
        notificationService.info(message);
    }

    /**
     * Show warning toast notification
     */
    showWarningToast(message: string): void {
        notificationService.warning(message);
    }

    /**
     * Example: logs and exceptions answer different questions, so a user-facing failure
     * deserves both.
     *
     * The exception side is deliberately thin here: HTTP failures are filed once, by the
     * axios interceptor, with endpoint-level grouping — `reportError` and `parseError` both
     * skip them to avoid double reporting. That leaves a real gap, because an issue records
     * that a request failed, never that we *interrupted someone* over it. This log closes
     * it: which surface we used, what wording they actually read, and whether it came from
     * a failed request or from client-side code.
     *
     * It's what turns "this endpoint 500s occasionally" into "this endpoint 500s and throws
     * a modal in the user's face every time".
     */
    private logUserFacingError(error: ErrorInput, parsed: ParsedError, surface: 'dialog' | 'toast'): void {
        log.error('User-facing error shown', logAttributes({
            'app.error.surface': surface,
            'app.error.message': parsed.message,
            'app.error.code': parsed.status?.data?.errorCode,
            'http.response.status_code': parsed.status?.status,
            // 'http' means the axios interceptor already filed the issue — match them up by
            // status and code. 'client' means `reportError` is the one that captured it.
            'app.error.kind': this.isAxiosError(error) ? 'http' : 'client'
        }));
    }

    /**
     * Parse any error type into a standardized format
     */
    private parseError(error: ErrorInput): ParsedError {
        // handle AxiosError
        if (this.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            const status = this.axiosErrorToStatus(axiosError);
            const message = this.getErrorMessage(status);
            // Already captured by the axios interceptor; see `reportError`.
            return { message, status, shouldCapture: false };
        }

        // handle standard Error objects
        if (error instanceof Error) {
            return {
                message: error.message || i18next.t('UPS_INTERNAL_ERROR'),
                shouldCapture: true
            };
        }

        // handle string errors
        if (typeof error === 'string') {
            return {
                message: error,
                shouldCapture: true
            };
        }

        // handle ErrorStatus objects
        if (this.isErrorStatus(error)) {
            const status = error as ErrorStatus;
            const message = this.getErrorMessage(status);
            const shouldCapture = !this.isNotEnoughActiveEmployees(status);
            return { message, status, shouldCapture };
        }

        // fallback for unknown error types
        return {
            message: i18next.t('UPS_INTERNAL_ERROR'),
            shouldCapture: true
        };
    }

    /**
     * Check if error is an AxiosError
     */
    private isAxiosError(error: unknown): error is AxiosError {
        const err = error as AxiosError;
        return err?.isAxiosError === true || (!!err?.response && !!err?.config);
    }

    /**
     * Check if error is an ErrorStatus
     */
    private isErrorStatus(error: unknown): error is ErrorStatus {
        return error !== null && typeof error === 'object' && ('status' in error || 'data' in error);
    }

    /**
     * Convert AxiosError to ErrorStatus
     */
    private axiosErrorToStatus(error: AxiosError): ErrorStatus {
        const response = error.response;

        if (!response) {
            // network error or request was cancelled
            return {
                status: 0,
                errorMessage: error.message
            };
        }

        return {
            status: response.status,
            data: response.data as ErrorStatus['data'],
            errorMessage: error.message
        };
    }

    /**
     * Get error message from status object
     */
    private getErrorMessage(status?: ErrorStatus): string {
        if (!status) {
            return i18next.t('UPS_INTERNAL_ERROR');
        }

        if (status.data?.customMessage) {
            return status.data.customMessage;
        }

        // Prefer API payload (errorCode/errorMessage) over Axios generic message
        const apiCode = status.data?.errorMessage || status.data?.errorCode;
        if (apiCode) {
            return this.getErrorStringFromCode(apiCode);
        }

        if (status.status === 0 || status.status === -1) {
            return i18next.t('CONNECTION_ERROR');
        }

        if (status.status === 401) {
            return i18next.t('EXPIRE_ERROR');
        }

        if (status.errorMessage) {
            return this.getErrorStringFromCode(status.errorMessage);
        }

        if (status.status === 500) {
            return i18next.t('UPS_INTERNAL_ERROR');
        }

        return status.status?.toString() || i18next.t('UPS_INTERNAL_ERROR');
    }
}

// export singleton instance
export const errorService = new ErrorService();

// export class for testing or custom instances
export default ErrorService;
